import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Progress } from "./ui/progress";
import { toast } from "sonner";
import { PROGRAMS } from "../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: "Your Goal" },
  { id: 2, label: "Property" },
  { id: 3, label: "Situation" },
  { id: 4, label: "Contact" },
];

const initialState = {
  program: "pre-foreclosure",
  property_address: "",
  property_condition: "",
  mortgage_status: "",
  timeline: "",
  situation: "",
  name: "",
  email: "",
  phone: "",
  consent: true,
};

export const LeadForm = () => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const progress = ((step - 1) / STEPS.length) * 100 + 25;

  const next = () => {
    if (step === 2 && !data.property_address.trim()) {
      toast.error("Please enter the property address.");
      return;
    }
    if (step === 4) {
      submit();
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    if (!data.name.trim() || !data.email.trim() || !data.phone.trim()) {
      toast.error("Please fill in your contact details.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, data);
      setSubmitted(true);
      toast.success("We received your request. We'll reach out within 24 hours.");
    } catch (e) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="lead-form"
        data-testid="lead-form-section"
        className="py-24 md:py-32 bg-brand-sage/40"
      >
        <div className="container-app max-w-2xl text-center">
          <div className="grid h-16 w-16 mx-auto place-items-center rounded-full bg-brand-forest text-brand-cream">
            <Check className="h-8 w-8" />
          </div>
          <h2
            data-testid="form-success"
            className="mt-8 font-heading text-4xl md:text-5xl font-bold text-brand-forest"
          >
            Your message is in good hands.
          </h2>
          <p className="mt-6 text-lg text-brand-muted leading-relaxed">
            We received your details and a member of our team will reach out
            within one business day. If your situation is urgent, text or call
            us directly.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="lead-form"
      data-testid="lead-form-section"
      className="py-24 md:py-32 bg-brand-sage/40"
    >
      <div className="container-app">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="overline mb-4">Free &amp; Confidential</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-brand-forest tracking-tight">
              Tell us about your property.
            </h2>
            <p className="mt-6 text-lg text-brand-muted leading-relaxed">
              A few quick questions — under 60 seconds. No credit pulls, no
              obligation. Just a private conversation about what's possible.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-brand-muted">
              {[
                "Your information stays private — always.",
                "Reply within one business day.",
                "Walk away anytime, no pressure.",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5">
                  <Check className="h-4 w-4 mt-0.5 text-brand-forest flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div
              data-testid="lead-form-card"
              className="bg-brand-cream border border-brand-line rounded-md p-6 md:p-10"
            >
              {/* Progress */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-brand-forest">
                    Step {step} of {STEPS.length} · {STEPS[step - 1].label}
                  </span>
                  <span className="text-sm text-brand-muted">
                    {Math.round(progress)}%
                  </span>
                </div>
                <Progress
                  value={progress}
                  className="h-2 bg-brand-line"
                  data-testid="form-progress"
                />
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {step === 1 && (
                    <Step1 data={data} set={set} />
                  )}
                  {step === 2 && (
                    <Step2 data={data} set={set} />
                  )}
                  {step === 3 && (
                    <Step3 data={data} set={set} />
                  )}
                  {step === 4 && (
                    <Step4 data={data} set={set} />
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 flex items-center justify-between gap-4">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={back}
                    data-testid="form-back"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-brand-muted hover:text-brand-forest transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={next}
                  disabled={submitting}
                  data-testid={step === STEPS.length ? "form-submit" : "form-next"}
                  className="btn-primary"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : step === STEPS.length ? (
                    <>
                      Submit Request
                      <ArrowRight className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Step1 = ({ data, set }) => (
  <div>
    <Label className="text-base font-heading font-semibold text-brand-forest mb-4 block">
      Which best describes your situation?
    </Label>
    <RadioGroup
      value={data.program}
      onValueChange={(v) => set("program", v)}
      className="space-y-3"
    >
      {PROGRAMS.map((p) => (
        <label
          key={p.id}
          htmlFor={`program-${p.id}`}
          data-testid={`option-${p.id}`}
          className={`flex items-start gap-4 p-4 rounded-md border cursor-pointer transition-all ${
            data.program === p.id
              ? "border-brand-forest bg-brand-forest/5"
              : "border-brand-line hover:border-brand-forest/40"
          }`}
        >
          <RadioGroupItem
            value={p.id}
            id={`program-${p.id}`}
            className="mt-1"
          />
          <div>
            <p className="font-heading font-semibold text-brand-forest">
              {p.label}
            </p>
            <p className="text-sm text-brand-muted mt-1">{p.short}</p>
          </div>
        </label>
      ))}
    </RadioGroup>
  </div>
);

const Step2 = ({ data, set }) => (
  <div className="space-y-5">
    <div>
      <Label htmlFor="address" className="text-sm font-semibold text-brand-forest">
        Property Address *
      </Label>
      <Input
        id="address"
        data-testid="input-address"
        placeholder="123 Main St, Orlando, FL 32801"
        value={data.property_address}
        onChange={(e) => set("property_address", e.target.value)}
        className="mt-2 h-12 bg-white border-brand-line focus-visible:ring-brand-forest"
      />
    </div>
    <div>
      <Label htmlFor="condition" className="text-sm font-semibold text-brand-forest">
        Property Condition
      </Label>
      <select
        id="condition"
        data-testid="input-condition"
        value={data.property_condition}
        onChange={(e) => set("property_condition", e.target.value)}
        className="mt-2 w-full h-12 rounded-md border border-brand-line bg-white px-3 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-forest"
      >
        <option value="">Select condition</option>
        <option value="excellent">Excellent — move-in ready</option>
        <option value="good">Good — minor cosmetic work</option>
        <option value="fair">Fair — needs some repairs</option>
        <option value="poor">Poor — major repairs needed</option>
        <option value="distressed">Distressed / damaged</option>
      </select>
    </div>
  </div>
);

const Step3 = ({ data, set }) => (
  <div className="space-y-5">
    <div>
      <Label htmlFor="mortgage" className="text-sm font-semibold text-brand-forest">
        Mortgage Status
      </Label>
      <select
        id="mortgage"
        data-testid="input-mortgage"
        value={data.mortgage_status}
        onChange={(e) => set("mortgage_status", e.target.value)}
        className="mt-2 w-full h-12 rounded-md border border-brand-line bg-white px-3 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-forest"
      >
        <option value="">Select status</option>
        <option value="current">Current on payments</option>
        <option value="late-30">30 days late</option>
        <option value="late-60">60 days late</option>
        <option value="late-90+">90+ days late</option>
        <option value="default-notice">Notice of default received</option>
        <option value="auction-set">Auction date scheduled</option>
        <option value="paid-off">Paid off / no mortgage</option>
      </select>
    </div>
    <div>
      <Label htmlFor="timeline" className="text-sm font-semibold text-brand-forest">
        Desired Timeline
      </Label>
      <select
        id="timeline"
        data-testid="input-timeline"
        value={data.timeline}
        onChange={(e) => set("timeline", e.target.value)}
        className="mt-2 w-full h-12 rounded-md border border-brand-line bg-white px-3 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-forest"
      >
        <option value="">Select timeline</option>
        <option value="asap">ASAP — urgent</option>
        <option value="30-days">Within 30 days</option>
        <option value="60-days">Within 60 days</option>
        <option value="90-days">Within 90 days</option>
        <option value="exploring">Just exploring options</option>
      </select>
    </div>
    <div>
      <Label htmlFor="situation" className="text-sm font-semibold text-brand-forest">
        Tell us anything else helpful
      </Label>
      <Textarea
        id="situation"
        data-testid="input-situation"
        rows={4}
        placeholder="E.g., I lost my job, I'm relocating, the home needs work..."
        value={data.situation}
        onChange={(e) => set("situation", e.target.value)}
        className="mt-2 bg-white border-brand-line focus-visible:ring-brand-forest"
      />
    </div>
  </div>
);

const Step4 = ({ data, set }) => (
  <div className="space-y-5">
    <div>
      <Label htmlFor="name" className="text-sm font-semibold text-brand-forest">
        Full Name *
      </Label>
      <Input
        id="name"
        data-testid="input-name"
        placeholder="Jane Doe"
        value={data.name}
        onChange={(e) => set("name", e.target.value)}
        className="mt-2 h-12 bg-white border-brand-line focus-visible:ring-brand-forest"
      />
    </div>
    <div>
      <Label htmlFor="email" className="text-sm font-semibold text-brand-forest">
        Email *
      </Label>
      <Input
        id="email"
        type="email"
        data-testid="input-email"
        placeholder="you@example.com"
        value={data.email}
        onChange={(e) => set("email", e.target.value)}
        className="mt-2 h-12 bg-white border-brand-line focus-visible:ring-brand-forest"
      />
    </div>
    <div>
      <Label htmlFor="phone" className="text-sm font-semibold text-brand-forest">
        Phone *
      </Label>
      <Input
        id="phone"
        type="tel"
        data-testid="input-phone"
        placeholder="(555) 555-5555"
        value={data.phone}
        onChange={(e) => set("phone", e.target.value)}
        className="mt-2 h-12 bg-white border-brand-line focus-visible:ring-brand-forest"
      />
    </div>
    <p className="text-xs text-brand-muted">
      By submitting, you agree to be contacted by J K Awesome Deeds LLC about
      your property. We never sell or share your info.
    </p>
  </div>
);
